import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import LoadingSpinner from '../partials/LoadingSpinner';

import {
  BlockStack, InlineStack, Text, Button, TextField, Badge, Banner, Box,
} from '@shopify/polaris';

import { searchProducts, productsSearchReset } from '../../redux/actions/products';

const optionText = (options) =>
  (options || []).map(option => `${option.option_display_name}: ${option.label}`).join(' / ');

// Catalog search for picking a packing rule's target. Selecting the
// product row targets the whole product; selecting one of its variants
// targets that variant - the rule's level is derived from which was
// picked.
const ProductSearchPanel = ({ selected, onSelect, onClear, products, submitSearch, resetSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Shopify pagination is cursor-based and forward-only; keep a stack so
  // the user can still step back through pages
  const [cursorStack, setCursorStack] = useState([]);

  useEffect(() => {
    return () => resetSearch();
  }, [resetSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim() !== '') {
        setCursorStack([]);
        submitSearch(searchTerm.trim(), null);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, submitSearch]);

  const nextPage = () => {
    if (!products.nextCursor) { return; }
    setCursorStack([...cursorStack, products.nextCursor]);
    submitSearch(searchTerm.trim(), products.nextCursor);
  }

  const prevPage = () => {
    const stack = [...cursorStack];
    stack.pop();
    setCursorStack(stack);
    submitSearch(searchTerm.trim(), stack.length > 0 ? stack[stack.length - 1] : null);
  }

  const selectedLabel = () => {
    if (!selected) { return ''; }

    let label = selected.productName || `Product ${selected.productId}`;
    if (selected.variantOptions?.length > 0) {
      label = `${label} (${optionText(selected.variantOptions)})`;
    } else if (selected.variantId) {
      label = `${label} (Variant ${selected.variantId})`;
    }

    return label;
  }

  const isSelected = (productId, variantId = null) => {
    if (!selected) { return false; }
    return String(selected.productId) === String(productId) && String(selected.variantId || '') === String(variantId || '');
  }

  const selectVariant = (product, variant) => {
    onSelect({
      productId: product.id,
      variantId: variant.id,
      productName: product.name,
      variantOptions: variant.option_values,
      sku: variant.sku
    });
  }

  const renderProductRow = (product) => {
    return (
      <Box
        key={`product-${product.id}`}
        borderColor="border"
        borderWidth="025"
        borderRadius="200"
        padding="300"
      >
        <InlineStack align="space-between" blockAlign="center" wrap={false}>
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            { product.image_url &&
              <img src={product.image_url} alt="" style={{ width: 40, height: 40, objectFit: 'cover' }} />
            }
            <BlockStack gap="100">
              <Text as="p" variant="bodyMd">{product.name}</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {product.sku ? `SKU ${product.sku}` : 'No SKU'}
                {product.variants.length > 0 && ` · ${product.variants.length} variant${product.variants.length === 1 ? '' : 's'}`}
              </Text>
            </BlockStack>
          </InlineStack>
          { isSelected(product.id)
            ? <Badge tone="success">Selected</Badge>
            : <Button onClick={() => onSelect({ productId: product.id, productName: product.name, sku: product.sku })}>
                Select product
              </Button>
          }
        </InlineStack>

        { product.variants.length > 0 &&
          <div style={{ marginLeft: 52, marginTop: 8 }}>
            { product.variants.map(variant =>
              <div key={variant.id} style={{ paddingTop: 4, paddingBottom: 4, borderTop: '1px solid #e3e6ea' }}>
                <InlineStack align="space-between" blockAlign="center" wrap={false}>
                  <BlockStack gap="100">
                    <Text as="p" variant="bodyMd">{optionText(variant.option_values) || variant.display_name || `Variant ${variant.id}`}</Text>
                    <Text as="p" variant="bodySm" tone="subdued">{variant.sku ? `SKU ${variant.sku}` : 'No SKU'}</Text>
                  </BlockStack>
                  { isSelected(product.id, variant.id)
                    ? <Badge tone="success">Selected</Badge>
                    : <Button onClick={() => selectVariant(product, variant)}>Select variant</Button>
                  }
                </InlineStack>
              </div>
            )}
          </div>
        }
      </Box>
    );
  }

  return (
    <BlockStack gap="300">
      <TextField
        label="Product search"
        labelHidden
        onChange={setSearchTerm}
        value={searchTerm}
        placeholder="Search products by name or SKU"
        autoComplete="off"
      />

      { selected &&
        <Banner tone="success" onDismiss={onClear}>
          <p>Target: {selectedLabel()}</p>
        </Banner>
      }

      { products.isSearching &&
        <LoadingSpinner size="small" statusText="Searching products" />
      }

      { !products.isSearching && products.searchedProducts !== null &&
        <BlockStack gap="200">
          { products.searchedProducts.map(renderProductRow) }
          { products.searchedProducts.length === 0 &&
            <Text as="p">No products matched that search.</Text>
          }

          { (products.nextCursor || cursorStack.length > 0) &&
            <InlineStack gap="200">
              <Button onClick={prevPage} disabled={cursorStack.length === 0}>
                Previous
              </Button>
              <Button onClick={nextPage} disabled={!products.nextCursor}>
                Next
              </Button>
            </InlineStack>
          }
        </BlockStack>
      }

      { products.errors &&
        <Banner tone="critical">
          <p>{products.errors}</p>
        </Banner>
      }
    </BlockStack>
  )
}

const mapStateToProps = state => ({
  products: state.products,
});

const mapDispatchToProps = (dispatch) => ({
  submitSearch: (term, cursor) => dispatch(searchProducts(term, cursor)),
  resetSearch: () => dispatch(productsSearchReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductSearchPanel);
